import type { NavGroup } from "./Sidebar";

/**
 * Единственный список разделов маркетинга; прямые ссылки сохраняют прежние адреса.
 * Порядок и иконки групп — как в меню прототипа «SMS-центр» (каркас CRM v2):
 * Кампании · Финансы · Контент · Клиенты и данные · Аналитика · Администрирование · Рабочее место.
 * Права пунктов прежние: «Финансы» — только с правом billing, «Счета партнёров» — администратору УК,
 * «Администрирование» — только администратору.
 */
export function navigationGroups({
  finance,
  clients,
  admin,
}: {
  finance: boolean;
  clients: boolean;
  admin: boolean;
}): NavGroup[] {
  return [
    {
      key: "campaigns",
      label: "Кампании",
      icon: "send",
      items: [
        { to: "/campaign-dashboard", label: "Обзор и заказы", icon: "bar" },
        {
          to: "/campaigns",
          label: "Кампании",
          icon: "grid",
          matchPrefixes: ["/campaigns"],
          excludePrefixes: ["/campaigns/results"],
          excludePattern: /^\/campaigns\/[^/]+\/results$/,
        },
        {
          to: "/campaigns/results",
          label: "Статистика кампании",
          icon: "bar",
          matchPattern: /^\/campaigns\/[^/]+\/results$/,
        },
        { to: "/recommendations", label: "Возвращаемость", icon: "refresh" },
      ],
    },
    ...(finance
      ? [
          {
            key: "finance" as const,
            label: "Финансы",
            icon: "wallet" as const,
            items: [
              {
                to: "/finance",
                label: "Обзор финансов",
                icon: "wallet" as const,
              },
              {
                to: "/billing",
                label: "Баланс и счета",
                icon: "card" as const,
              },
              { to: "/spending", label: "Расходы", icon: "bar" as const },
              ...(clients
                ? [
                    {
                      to: "/clients",
                      label: "Счета партнёров",
                      icon: "users" as const,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    {
      key: "content",
      label: "Контент",
      icon: "layout",
      items: [
        {
          to: "/templates",
          label: "Шаблоны сообщений",
          icon: "doc",
          matchPrefixes: ["/templates"],
        },
        { to: "/promo-codes", label: "Промокоды", icon: "gift" },
      ],
    },
    {
      key: "data",
      label: "Клиенты и данные",
      icon: "users",
      items: [
        { to: "/guests", label: "Клиенты", icon: "users" },
        { to: "/data/actions", label: "Действия клиентов" },
        { to: "/data/orders", label: "Заказы" },
        { to: "/data/products", label: "Продукты" },
        { to: "/segments", label: "RFM-сегменты" },
      ],
    },
    {
      key: "analytics",
      label: "Аналитика",
      icon: "bar",
      items: [
        { to: "/reports/business", label: "Метрики бизнеса" },
        { to: "/reports/goals", label: "Цели бизнеса" },
        { to: "/reports/actions", label: "Целевые действия" },
        { to: "/reports/lifecycle", label: "Жизненный цикл" },
        { to: "/reports/segments", label: "Отчёт по сегментам" },
        { to: "/reports/promotions", label: "Отчёт по акциям" },
        { to: "/reports/scenarios", label: "Отчёт по сценариям" },
        { to: "/reports/site", label: "Конверсия сайта" },
        { to: "/analytics", label: "Повторные покупки и LTV" },
      ],
    },
    ...(admin
      ? [
          {
            key: "settings" as const,
            label: "Администрирование",
            icon: "cog" as const,
            items: [
              {
                to: "/settings",
                label: "Настройки",
                icon: "cog" as const,
                matchPrefixes: ["/channels"],
                end: true,
              },
              {
                to: "/settings/integrations",
                label: "Внешние системы",
                icon: "cog" as const,
              },
              {
                to: "/settings/staff",
                label: "Персонал",
                icon: "users" as const,
              },
              {
                to: "/settings/security",
                label: "Группы безопасности",
                icon: "users" as const,
              },
            ],
          },
        ]
      : []),
    {
      key: "workspace",
      label: "Рабочее место",
      icon: "briefcase",
      items: [
        { to: "/preferences", label: "Форматы и расчёты" },
        { to: "/problems", label: "Проблемы и развитие" },
        { to: "/howto", label: "Как это работает", icon: "book" },
      ],
    },
  ];
}
